/**
 * Pure, framework-free analytics queue (cue-forge-do7.1). No globals, no DOM,
 * no timers of its own — everything is injected so the batching, opt-out, and
 * wire-serialization logic is unit-testable in isolation. `client.ts` wires the
 * real browser transport, storage, timers, and crypto.
 *
 * Inert whenever the endpoint is unset or the user has opted out: `track`/`flush`
 * no-op, nothing is stored, nothing is sent. All storage access is wrapped so a
 * throwing localStorage (private mode) degrades to inert rather than throwing
 * into app code.
 */
import type { EventName, EventProps } from './catalog';

const ID_KEY = 'cueforge.analytics.id';
const OPTOUT_KEY = 'cueforge.analytics.optout';

const FLUSH_AT = 20;
const FLUSH_DELAY_MS = 10_000;
const MAX_EVENTS_PER_REQUEST = 25;
const MAX_PROPS = 8;
const MAX_KEY_LEN = 32;
const MAX_VALUE_LEN = 64;

interface QueuedEvent {
  n: EventName;
  t: number;
  p: Record<string, string>;
}

export interface Analytics {
  /** Whether an endpoint is configured. False => wholly inert. */
  readonly enabled: boolean;
  track<N extends EventName>(name: N, props: EventProps[N]): void;
  flush(): void;
  isOptedOut(): boolean;
  setOptOut(v: boolean): void;
}

export interface AnalyticsDeps {
  endpoint: string | undefined;
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
  /** Transport. Fire-and-forget; the client never reads the response. */
  send: (url: string, body: string) => void;
  now: () => number;
  uuid: () => string;
  setTimer: (fn: () => void, ms: number) => unknown;
  clearTimer: (t: unknown) => void;
}

export function createAnalytics(deps: AnalyticsDeps): Analytics {
  const endpoint = deps.endpoint;
  const enabled = typeof endpoint === 'string' && endpoint.length > 0;

  const queue: QueuedEvent[] = [];
  let timer: unknown = null;

  function readItem(key: string): string | null {
    try {
      return deps.storage.getItem(key);
    } catch {
      return null;
    }
  }
  function writeItem(key: string, value: string): void {
    try {
      deps.storage.setItem(key, value);
    } catch {
      // private mode / disabled storage — degrade to inert.
    }
  }
  function deleteItem(key: string): void {
    try {
      deps.storage.removeItem(key);
    } catch {
      // ignore
    }
  }

  function isOptedOut(): boolean {
    return readItem(OPTOUT_KEY) === '1';
  }

  function cancelTimer(): void {
    if (timer !== null) {
      deps.clearTimer(timer);
      timer = null;
    }
  }

  /** Defensive wire hardening — the types are the primary guard. */
  function sanitizeProps(props: Record<string, unknown>): Record<string, string> {
    const out: Record<string, string> = {};
    let count = 0;
    for (const key of Object.keys(props)) {
      if (count >= MAX_PROPS) break;
      const raw = props[key];
      let value: string;
      if (typeof raw === 'string') value = raw;
      else if (typeof raw === 'number' || typeof raw === 'boolean') value = String(raw);
      else continue; // undefined/null/object/symbol — not representable on the wire
      out[key.slice(0, MAX_KEY_LEN)] = value.slice(0, MAX_VALUE_LEN);
      count++;
    }
    return out;
  }

  function ensureId(): string {
    const existing = readItem(ID_KEY);
    if (existing) return existing;
    const id = deps.uuid();
    writeItem(ID_KEY, id);
    return id;
  }

  function track<N extends EventName>(name: N, props: EventProps[N]): void {
    if (!enabled || isOptedOut()) return;
    queue.push({ n: name, t: deps.now(), p: sanitizeProps(props) });
    if (queue.length >= FLUSH_AT) {
      flush();
      return;
    }
    if (timer === null) {
      timer = deps.setTimer(() => {
        timer = null;
        flush();
      }, FLUSH_DELAY_MS);
    }
  }

  function flush(): void {
    cancelTimer();
    if (!enabled || endpoint === undefined || queue.length === 0) return;
    if (isOptedOut()) {
      queue.length = 0;
      return;
    }
    const id = ensureId();
    const batch = queue.splice(0, queue.length);
    for (let i = 0; i < batch.length; i += MAX_EVENTS_PER_REQUEST) {
      const events = batch.slice(i, i + MAX_EVENTS_PER_REQUEST);
      deps.send(endpoint, JSON.stringify({ v: 1, id, events }));
    }
  }

  function setOptOut(v: boolean): void {
    if (v) {
      writeItem(OPTOUT_KEY, '1');
      deleteItem(ID_KEY);
      queue.length = 0;
      cancelTimer();
    } else {
      deleteItem(OPTOUT_KEY);
    }
  }

  return {
    enabled,
    track,
    flush,
    isOptedOut,
    setOptOut,
  };
}
